// Shared icon-library picker data — used by hero's `features` items (Step 11)
// and ourAdvantage's `icon` card field (Step 11 migration off raw emoji).
// Built once, reused everywhere an icon needs to be admin-selectable —
// see plan.md "Shared icon-library picker".
//
// Curated subset of AntD icons (~40-60, food/delivery/health/time themed),
// not the full ~800-icon set, PLUS a curated ~110-icon subset of react-icons'
// Game Icons (Gi) set — AntD has almost no literal food/restaurant glyphs
// (no pizza/burger/fish/plate/chef-hat shapes exist there at all), so real
// restaurant iconography comes from Gi instead, kept in its own block below
// rather than force-fit into AntD's set. Stable icon-name strings (e.g.
// "AppleOutlined", "GiPizzaSlice") are stored in config — same asset-id-style
// indirection as every other picker in this system (hero's `image`, navbar's
// `logo`): gwt-admin resolves the name directly to its own icon component;
// meelz-client-v2 maps the same name to its own icon library
// (lucide-react/react-icons) via a per-repo name-mapping table, with a safe
// default icon for any unmapped name. E4F/CK are NOT wired to resolve the Gi
// names today (react-icons isn't a dependency in either repo) — scoped out
// for now, can be added later if those brands need this set live too.
// CreditCardOutlined/InboxOutlined/WalletOutlined were previously MISSING
// from this file entirely, despite being the real, live icon values on 9
// of faqLayouts.constants.js's own seeded FAQ items (Tariffs/Payment/
// Storage categories) — meaning those items' icons could never resolve
// here (this file's own resolveIconComponent/CardIconGlyph render the raw
// name as text) NOR in the IconPickerField (not offered as a re-pickable
// option), and every client repo inherited the same gap. Confirmed live:
// admin's own preview showed literal "WalletOutlined" text. Added here so
// the picker's own curated set is a superset of every icon any seeded
// content actually uses, not just what was manually added over time.
import {
    AimOutlined,
    AppleOutlined,
    BulbOutlined,
    CalendarOutlined,
    CarOutlined,
    CarryOutOutlined,
    CheckCircleOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    CoffeeOutlined,
    CreditCardOutlined,
    CrownOutlined,
    CustomerServiceOutlined,
    DashboardOutlined,
    EnvironmentOutlined,
    ExperimentOutlined,
    FireOutlined,
    FundOutlined,
    GiftOutlined,
    GoldOutlined,
    HeartOutlined,
    HomeOutlined,
    InboxOutlined,
    LikeOutlined,
    MedicineBoxOutlined,
    QuestionCircleOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SafetyOutlined,
    ScheduleOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    ShoppingOutlined,
    SmileOutlined,
    StarOutlined,
    SunOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
    TruckOutlined,
    WalletOutlined,
} from "@ant-design/icons";
import {
    GiAcorn,
    GiAppleCore,
    GiAvocado,
    GiBacon,
    GiBanana,
    GiBananaBunch,
    GiBeerBottle,
    GiBeerStein,
    GiBellPepper,
    GiBerriesBowl,
    GiBigEgg,
    GiBowlOfRice,
    GiBread,
    GiBreadSlice,
    GiBroccoli,
    GiBubblingBowl,
    GiButter,
    GiButterToast,
    GiCakeSlice,
    GiCampCookingPot,
    GiCandyCanes,
    GiCannedFish,
    GiCarrot,
    GiChampagneCork,
    GiCheeseWedge,
    GiChefToque,
    GiCherry,
    GiChicken,
    GiChickenLeg,
    GiChiliPepper,
    GiChocolateBar,
    GiCoffeeBeans,
    GiCoffeeCup,
    GiCoffeeMug,
    GiCoffeePot,
    GiCook,
    GiCookie,
    GiCookingPot,
    GiCorn,
    GiCornucopia,
    GiCrab,
    GiCrabClaw,
    GiCroissant,
    GiCupcake,
    GiCutLemon,
    GiDonut,
    GiDrinking,
    GiDuck,
    GiEggClutch,
    GiFastNoodles,
    GiFishCooked,
    GiFishing,
    GiFishingHook,
    GiFlatfish,
    GiFoodTruck,
    GiFrenchFries,
    GiFriedEggs,
    GiFriedFish,
    GiFruitBowl,
    GiFullPizza,
    GiGarlic,
    GiGingerbreadMan,
    GiGrapes,
    GiHamburger,
    GiHamShank,
    GiHoneycomb,
    GiHoneyJar,
    GiHoneypot,
    GiHotDog,
    GiIceCreamCone,
    GiIceCreamScoop,
    GiJellyfish,
    GiKitchenKnives,
    GiKitchenScale,
    GiKiwiFruit,
    GiLemon,
    GiManualJuicer,
    GiMeat,
    GiMeatCleaver,
    GiMilkCarton,
    GiMushroom,
    GiMushrooms,
    GiNoodleBall,
    GiNoodles,
    GiOpenedFoodCan,
    GiOrange,
    GiOrangeSlice,
    GiOyster,
    GiPineapple,
    GiPizzaCutter,
    GiPizzaSlice,
    GiPopcorn,
    GiPotato,
    GiPressureCooker,
    GiPretzel,
    GiRawEgg,
    GiRiceCooker,
    GiRoastChicken,
    GiSandwich,
    GiSausage,
    GiShrimp,
    GiSlicedBread,
    GiSlicedMushroom,
    GiSteak,
    GiStrawberry,
    GiSushis,
    GiTacos,
    GiTeapot,
    GiTomato,
    GiTropicalFish,
    GiWatermelon,
    GiWineBottle,
    GiWineGlass,
} from "react-icons/gi";

// Wellness-quiz-relevant icons (weight/fitness goals, sleep, hydration,
// energy, activity level, diet restrictions/allergies) — AntD's Outlined set
// has almost none of these (checked: no scale/dumbbell/sleep/water-drop/
// battery glyphs exist there at all), so these come from lucide-react
// instead, same reasoning as the Gi food-icon block above. Named with an
// "Lu" prefix (mirrors the "Gi" convention) and mapped 1:1 by name to
// meelz-client-v2's HERO_ICON_MAP, which already imports these same
// lucide-react components for the pre-existing AntD-name entries — no new
// per-repo mapping mechanism, just more shared names.
import {
    Scale as LuScale,
    Dumbbell as LuDumbbell,
    Footprints as LuFootprints,
    Bed as LuBed,
    Droplet as LuDroplet,
    Droplets as LuDroplets,
    Battery as LuBattery,
    Salad as LuSalad,
    Activity as LuActivity,
    HeartPulse as LuHeartPulse,
    Flame as LuFlame,
    TrendingUp as LuTrendingUp,
    TrendingDown as LuTrendingDown,
    Bike as LuBike,
    Meh as LuMeh,
    Ban as LuBan,
    ShieldAlert as LuShieldAlert,
    Baby as LuBaby,
    GraduationCap as LuGraduationCap,
    Laptop as LuLaptop,
    Sofa as LuSofa,
    Briefcase as LuBriefcase,
    CandyOff as LuCandyOff,
    HeartCrack as LuHeartCrack,
    Gauge as LuGauge,
} from "lucide-react";

// Name → component lookup, used by IconPickerField and anywhere a stored
// icon name needs to render as an actual icon.
export const ICON_LIBRARY = {
    AimOutlined,
    AppleOutlined,
    BulbOutlined,
    CalendarOutlined,
    CarOutlined,
    CarryOutOutlined,
    CheckCircleOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    CoffeeOutlined,
    CreditCardOutlined,
    CrownOutlined,
    CustomerServiceOutlined,
    DashboardOutlined,
    EnvironmentOutlined,
    ExperimentOutlined,
    FireOutlined,
    FundOutlined,
    GiftOutlined,
    GoldOutlined,
    HeartOutlined,
    HomeOutlined,
    InboxOutlined,
    LikeOutlined,
    MedicineBoxOutlined,
    QuestionCircleOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SafetyOutlined,
    ScheduleOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    ShoppingOutlined,
    SmileOutlined,
    StarOutlined,
    SunOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
    TruckOutlined,
    WalletOutlined,
    GiAcorn,
    GiAppleCore,
    GiAvocado,
    GiBacon,
    GiBanana,
    GiBananaBunch,
    GiBeerBottle,
    GiBeerStein,
    GiBellPepper,
    GiBerriesBowl,
    GiBigEgg,
    GiBowlOfRice,
    GiBread,
    GiBreadSlice,
    GiBroccoli,
    GiBubblingBowl,
    GiButter,
    GiButterToast,
    GiCakeSlice,
    GiCampCookingPot,
    GiCandyCanes,
    GiCannedFish,
    GiCarrot,
    GiChampagneCork,
    GiCheeseWedge,
    GiChefToque,
    GiCherry,
    GiChicken,
    GiChickenLeg,
    GiChiliPepper,
    GiChocolateBar,
    GiCoffeeBeans,
    GiCoffeeCup,
    GiCoffeeMug,
    GiCoffeePot,
    GiCook,
    GiCookie,
    GiCookingPot,
    GiCorn,
    GiCornucopia,
    GiCrab,
    GiCrabClaw,
    GiCroissant,
    GiCupcake,
    GiCutLemon,
    GiDonut,
    GiDrinking,
    GiDuck,
    GiEggClutch,
    GiFastNoodles,
    GiFishCooked,
    GiFishing,
    GiFishingHook,
    GiFlatfish,
    GiFoodTruck,
    GiFrenchFries,
    GiFriedEggs,
    GiFriedFish,
    GiFruitBowl,
    GiFullPizza,
    GiGarlic,
    GiGingerbreadMan,
    GiGrapes,
    GiHamburger,
    GiHamShank,
    GiHoneycomb,
    GiHoneyJar,
    GiHoneypot,
    GiHotDog,
    GiIceCreamCone,
    GiIceCreamScoop,
    GiJellyfish,
    GiKitchenKnives,
    GiKitchenScale,
    GiKiwiFruit,
    GiLemon,
    GiManualJuicer,
    GiMeat,
    GiMeatCleaver,
    GiMilkCarton,
    GiMushroom,
    GiMushrooms,
    GiNoodleBall,
    GiNoodles,
    GiOpenedFoodCan,
    GiOrange,
    GiOrangeSlice,
    GiOyster,
    GiPineapple,
    GiPizzaCutter,
    GiPizzaSlice,
    GiPopcorn,
    GiPotato,
    GiPressureCooker,
    GiPretzel,
    GiRawEgg,
    GiRiceCooker,
    GiRoastChicken,
    GiSandwich,
    GiSausage,
    GiShrimp,
    GiSlicedBread,
    GiSlicedMushroom,
    GiSteak,
    GiStrawberry,
    GiSushis,
    GiTacos,
    GiTeapot,
    GiTomato,
    GiTropicalFish,
    GiWatermelon,
    GiWineBottle,
    GiWineGlass,
    LuScale,
    LuDumbbell,
    LuFootprints,
    LuBed,
    LuDroplet,
    LuDroplets,
    LuBattery,
    LuSalad,
    LuActivity,
    LuHeartPulse,
    LuFlame,
    LuTrendingUp,
    LuTrendingDown,
    LuBike,
    LuMeh,
    LuBan,
    LuShieldAlert,
    LuBaby,
    LuGraduationCap,
    LuLaptop,
    LuSofa,
    LuBriefcase,
    LuCandyOff,
    LuHeartCrack,
    LuGauge,
};

export const ICON_LIBRARY_NAMES = Object.keys(ICON_LIBRARY);

// Resolves a stored icon name to its component, or `null` if the name isn't
// recognized (e.g. a stale/never-picked value, or ourAdvantage's pre-
// migration raw emoji strings) — callers render their own fallback/default,
// never crash on an unrecognized name.
export function resolveIconComponent(name) {
    return ICON_LIBRARY[name] || null;
}
